import React, { useState } from "react";
import { registerUser, loginUserDB } from "../utils/db";
import { Eye, EyeOff } from "lucide-react";
import "./LoginModal.css";
import modalBg from "../assets/login-bg.jpg"; 

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (email: string) => void;
}

export const LoginModal = ({ isOpen, onClose, onLoginSuccess }: LoginModalProps) => {
  const [isActive, setIsActive] = useState(false); 
  const [showPassword, setShowPassword] = useState(false); 
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const success = await loginUserDB(email, password);
      if (success) {
        onLoginSuccess(email);
        onClose();
      } else {
        setError("Invalid email or password");
      }
    } catch (err: any) {
      setError("An error occurred during login");
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await registerUser(email, password);
      // Success: Clear fields and slide back to login
      togglePanel(false); 
      alert("Registration Successful! Please login.");
    } catch (err: any) {
      setError(err || "Registration failed");
    }
  };

  // FIX: Resets all input states when switching panels
  const togglePanel = (toActive: boolean) => {
    setIsActive(toActive);
    setError("");
    setShowPassword(false);
    // Clear all fields so old data doesn't persist
    setEmail("");
    setPassword("");
    setName("");
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div 
        className={`auth-sliding-container ${isActive ? "active" : ""}`} 
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* --- SIGN UP FORM (REGISTER) --- */}
        <div className="auth-form-container sign-up">
          <form onSubmit={handleRegister} autoComplete="off">
            <h1 className="auth-title">Create Account</h1>
            <p className="auth-subtitle">Join us to generate professional payslips</p>
            
            <div className="auth-input-group">
              <input 
                type="text" 
                placeholder="Full Name" 
                required 
                autoComplete="off"
                value={name} 
                onChange={(e) => setName(e.target.value)} 
              />
            </div>
            
            <div className="auth-input-group">
              <input 
                type="email" 
                placeholder="Email" 
                required 
                autoComplete="off"
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>

            <div className="auth-input-group password-wrapper">
              <input 
                type={showPassword ? "text" : "password"} 
                placeholder="Password" 
                required 
                autoComplete="new-password" // Prevents browser from suggesting old passwords
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
              />
              <button 
                type="button" className="pwd-toggle" 
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && isActive && <p className="auth-error">{error}</p>}
            <button type="submit" className="auth-btn-primary">Sign Up</button>
          </form>
        </div>

        {/* --- SIGN IN FORM (LOGIN) --- */}
        <div className="auth-form-container sign-in">
          <form onSubmit={handleLogin}>
            <h1 className="auth-title">Sign In</h1>
            <p className="auth-subtitle">Access your saved history and settings</p>
            
            <div className="auth-input-group">
              <input 
                type="email" 
                placeholder="Email" 
                required 
                autoComplete="email"
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>

            <div className="auth-input-group password-wrapper">
              <input 
                type={showPassword ? "text" : "password"} 
                placeholder="Password" 
                required 
                autoComplete="current-password"
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
              />
              <button 
                type="button" className="pwd-toggle" 
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <a href="#" className="auth-link">Forgot your password?</a>
            {error && !isActive && <p className="auth-error">{error}</p>}
            <button type="submit" className="auth-btn-primary">Sign In</button>
          </form>
        </div>

        {/* --- TOGGLE OVERLAY --- */}
        <div className="auth-toggle-container">
          <div className="auth-toggle-overlay" style={{ backgroundImage: `url(${modalBg})` }}>
            
            <div className="auth-toggle-panel panel-left">
              <h1>Welcome Back!</h1>
              <p>To stay connected with us please login with your personal info</p>
              <button className="auth-btn-ghost" onClick={() => togglePanel(false)}>
                Sign In
              </button>
            </div>

            <div className="auth-toggle-panel panel-right">
              <h1>Hello, Friend!</h1>
              <p>Enter your details and start generating professional payslips today</p>
              <button className="auth-btn-ghost" onClick={() => togglePanel(true)}>
                Sign Up
              </button>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};